import React from 'react';
import styled from 'styled-components';

import { QUERIES } from '../constants/mediaQueries';
import { CONTACT_URL, EMAIL_ADDRESS } from '../constants/contactData';
import {
  FOOTER_QUICK_LINKS,
  FOOTER_RESOURCE_LINKS,
  FOOTER_LEGAL_LINKS,
  SOCIAL_LINKS,
} from '../constants/navigationLinks';
import { FOOTER_FIELDS } from '../constants/formFields';
import { getInitialData } from '../utils/dateUtils';
import LogoWhite from './LogoWhite';
import LogoYellow from './LogoYellow';
import Form from './Form';
import Links from './Links';
import Button from './Button';

const FooterStyles = styled.footer`
  background-color: var(--color-primary-darker);
  border-top: var(--border-default);
  padding: var(--spacing-small) var(--spacing-large);

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    color: var(--color-neutral-white);
  }
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--spacing-xlarge);
  align-items: start;

  ${QUERIES.maxTablet} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${QUERIES.maxMobile} {
    grid-template-columns: 1fr;
  }
`;

const ContactSection = styled.div`
  display: grid;
  gap: var(--spacing-small);
  grid-column: span 2;

  ${QUERIES.maxTablet} {
    grid-column: 1 / -1;
  }
`;

const SectionHeading = styled.h6`
  font-weight: var(--weight-bold);
`;

const ContactTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xsmall);
`;

const EmailButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const LinksSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${QUERIES.maxMobile} {
    grid-column: span 2;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-xlarge);
  }
`;

const FooterMiddle = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: var(--spacing-base);
  margin-top: var(--spacing-base);
`;

const LogoWrapper = styled.div`
  width: 100px;
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  margin-top: var(--spacing-small);
`;

const Copyright = styled.p`
  color: var(--color-neutral-lighter);
  font-size: var(--body-tiny-size);
`;

const Footer: React.FC = () => {
  const { currentYear } = getInitialData();

  return (
    <FooterStyles>
      <FooterTop>
        <ContactSection>
          <h6>Contact Me</h6>
          <ContactTextWrapper>
            <p>Have a question or want to work together?</p>
            <EmailButtonWrapper>
              <p>Email:</p>
              <Button
                href={CONTACT_URL.email}
                text={EMAIL_ADDRESS}
                size="xxsmall"
                textSize="small"
                colorScheme="scheme6"
              />
            </EmailButtonWrapper>
          </ContactTextWrapper>
          <Form
            formName="footer"
            fields={FOOTER_FIELDS}
            submitButtonText="Message Me"
            colorScheme="scheme2"
            buttonColourScheme="scheme2"
            inline={true}
            animate={false}
            className="footer-form"
          />
        </ContactSection>

        <LinksSection>
          <h6>Resources</h6>
          <Links
            links={FOOTER_RESOURCE_LINKS}
            vertical={true}
            gap="small"
            buttonStyles={{ size: 'small', textSize: 'small', colorScheme: 'scheme6' }}
            target="_blank"
            rel="noopener noreferrer"
          />
        </LinksSection>

        <LinksSection>
          <h6>Quick Links</h6>
          <Links
            links={FOOTER_QUICK_LINKS}
            vertical={true}
            gap="small"
            buttonStyles={{ size: 'small', textSize: 'small', colorScheme: 'scheme6' }}
          />
        </LinksSection>

        <LinksSection>
          <h6>Social Links</h6>
          <Links
            links={SOCIAL_LINKS}
            vertical={true}
            gap="small"
            buttonStyles={{
              colorScheme: 'scheme6',
              iconColour: 'scheme6',
              iconSize: 'xxlarge',
              iconPosition: 'icon-only',
            }}
            target="_blank"
            rel="noopener noreferrer"
          />
        </LinksSection>
      </FooterTop>

      <FooterMiddle>
        <LogoWrapper>
          <LogoYellow />
        </LogoWrapper>
        <Links
          links={FOOTER_LEGAL_LINKS}
          gap="small"
          buttonStyles={{ size: 'xxsmall', textSize: 'xsmall', colorScheme: 'scheme6' }}
        />
      </FooterMiddle>

      <FooterBottom>
        <Copyright>&copy; {currentYear} ScriptedbyKat. All rights reserved.</Copyright>
      </FooterBottom>
    </FooterStyles>
  );
};

export default Footer;
